/**
 * useVoiceChat — React hook for mesh voice chat in DrawLab.
 *
 * Wraps MeshVoiceManager into React-friendly state and actions. Hides the
 * complexity of multiple peer connections behind a simple API.
 *
 * Usage:
 *   const { status, peers, isMuted, joinVoice, leaveVoice, toggleMute } = useVoiceChat();
 *
 * State machine:
 *   'disabled'   — voice chat not active
 *   'connecting' — acquiring mic permission, joining
 *   'connected'  — in voice chat
 *   'error'      — failed (permission denied, etc.)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useApp } from '@context/AppContext';
import { MeshVoiceManager, MESH_VOICE_STATE } from '@/services/webrtc/MeshVoiceManager';

export const VOICE_STATUS = MESH_VOICE_STATE;

export function useVoiceChat() {
  const { state } = useApp();
  const { user, room } = state;

  const [status, setStatus] = useState(VOICE_STATUS.IDLE);
  const [peers, setPeers] = useState([]);           // [{userId, nickname}]
  const [remoteStreams, setRemoteStreams] = useState(new Map()); // userId → MediaStream
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState(null);

  const managerRef = useRef(null);

  // ── Clean up on unmount ───────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (managerRef.current) {
        managerRef.current.leave().catch(() => {});
        managerRef.current = null;
      }
    };
  }, []);

  // ── Join voice chat ───────────────────────────────────────────────────────
  const joinVoice = useCallback(async () => {
    if (managerRef.current) return;
    if (!room.code || !user.id) {
      setError(new Error('Not in a room yet'));
      return;
    }

    setError(null);
    setStatus(VOICE_STATUS.CONNECTING);

    const manager = new MeshVoiceManager(room.code, user.id, user.nickname);

    manager.onStateChange = (s) => setStatus(s);
    manager.onPeersChanged = (list) => setPeers(list);
    manager.onRemoteStreamAdded = (peerId, stream) => {
      setRemoteStreams((prev) => {
        const next = new Map(prev);
        next.set(peerId, stream);
        return next;
      });
    };
    manager.onRemoteStreamRemoved = (peerId) => {
      setRemoteStreams((prev) => {
        const next = new Map(prev);
        next.delete(peerId);
        return next;
      });
    };
    manager.onError = (err) => {
      setError(err);
      setStatus(VOICE_STATUS.ERROR);
    };

    managerRef.current = manager;
    await manager.join();
  }, [room.code, user.id, user.nickname]);

  // ── Leave voice chat ──────────────────────────────────────────────────────
  const leaveVoice = useCallback(async () => {
    if (!managerRef.current) return;
    await managerRef.current.leave();
    managerRef.current = null;
    setPeers([]);
    setRemoteStreams(new Map());
    setIsMuted(false);
    setStatus(VOICE_STATUS.IDLE);
  }, []);

  // ── Toggle mute ───────────────────────────────────────────────────────────
  const toggleMute = useCallback(() => {
    if (!managerRef.current) return;
    const muted = managerRef.current.toggleMute();
    setIsMuted(muted);
  }, []);

  return {
    status,
    peers,
    remoteStreams,
    isMuted,
    error,
    joinVoice,
    leaveVoice,
    toggleMute,
    isActive: status === VOICE_STATUS.CONNECTED || status === VOICE_STATUS.CONNECTING,
  };
}
